// Coach-Governed Continuity Loop — pure view-model for continuity tension detection.
// No side effects. No DB calls. No auto-resolution.
// Surfaces tensions for coach review only; never resolves them.

import type { CoachInsight } from "@/hooks/useCoachChat";
import type { ContinuityReview } from "@/lib/behavior/coachContinuityReviewService";
import type { ContinuityTension, ContinuityTensionType } from "@/types/continuityTension";

export const MIN_KEYWORD_LENGTH = 4;

const STOP_WORDS = new Set([
  "the", "and", "that", "this", "with", "from", "have", "been",
  "will", "when", "they", "were", "their", "what", "also", "into", "about",
]);

function extractKeywords(text: string): string[] {
  return text
    .toLowerCase()
    .split(/\W+/)
    .filter((token) => token.length >= MIN_KEYWORD_LENGTH && !STOP_WORDS.has(token));
}

export function buildContinuityTensions(
  coachInsight: CoachInsight | null,
  carriedForwardReviews: ContinuityReview[],
): ContinuityTension[] {
  if (coachInsight === null || carriedForwardReviews.length === 0) {
    return [];
  }

  const positiveText = [
    coachInsight.situation,
    coachInsight.coachMove,
    coachInsight.interpretation,
    ...coachInsight.questions,
  ]
    .join(" ")
    .toLowerCase();

  const watchoutText = [
    coachInsight.watchout,
    ...coachInsight.missingData,
  ]
    .join(" ")
    .toLowerCase();

  const missingDataText = coachInsight.missingData.join(" ").toLowerCase();

  const tensions: ContinuityTension[] = [];

  for (const review of carriedForwardReviews) {
    const displayTitle = review.reframedTitle ?? review.originalTitle;
    const keywords = extractKeywords(displayTitle);

    if (keywords.length === 0) continue;

    const positiveHit = keywords.some((kw) => positiveText.includes(kw));
    const watchoutHit = keywords.some((kw) => watchoutText.includes(kw));
    const missingHit = keywords.some((kw) => missingDataText.includes(kw));

    let type: ContinuityTensionType | null = null;
    let description = "";

    if (missingHit) {
      type = "evidence_changed";
      description = `'${displayTitle}' is now flagged as missing data in the current insight.`;
    } else if (positiveHit && watchoutHit) {
      type = "mixed_evidence";
      description = `'${displayTitle}' appears in both the current focus and the watchout.`;
    } else if (!positiveHit && !watchoutHit) {
      type = "agenda_mismatch";
      description = `'${displayTitle}' does not appear in the current session's focus.`;
    }

    // Note: if watchoutHit=true but positiveHit=false (topic appears only in watchout),
    // no tension is produced. Product decision: a topic that appears solely in the watchout
    // is relevant background context for the coach, not a continuity conflict requiring
    // coach action. Surfacing it as a tension would create noise without actionable signal.

    if (type !== null) {
      tensions.push({
        id: `${review.id}:${type}`,
        type,
        reviewId: review.id,
        reviewTitle: displayTitle,
        description,
      });
    }
  }

  return tensions;
}
